import currency from "currency.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as ArrowDown } from "../../../assets/svg/arrow-down.svg";
import { SideResponse } from "../../../codegen-api";
import { COMPONENTS } from "../../../constants/design/spacing";
import { INDEX_PRICE_COLLAR_PERPS } from "../../../constants/precision/form";
import { useOrder } from "../../../hooks/api/order/useOrder";
import usePrevious from "../../../hooks/usePrevious";
import useOrderbookWSS from "../../../hooks/wss/useOrderbookWSS";
import useTradesWSS from "../../../hooks/wss/useTradesWSS";
import { rawToDecimal } from "../../../utils/rawConversion";
import { useMarketDecimals } from "../../../hooks/useMarketDecimals";
import { IPriceSize } from "../../../interfaces/Orderbook";
import {
  computeSpreadStats,
  groupByTicketSize,
} from "../../../utils/orderbook";
import TradeOrderbook from "../../TicketDetails/TradeOrderBook";
import {
  MidMetric,
  MidMetricLabel,
  MidMetricValue,
  MidRow,
} from "../../TicketDetails/TradeOrderBook/style";
import { OrderbookFilterEnum } from "./shared";
import { ScrollableContent, ScrollableWrapper } from "./style";

const looksRawInteger = (val: string | number | undefined): boolean => {
  if (val === undefined || val === null) return false;
  const str = val.toString();
  if (str.includes(".")) return false;
  return /^\d{10,}$/.test(str);
};

interface ITradeSectionProps {
  onOrderbookRowClick: (
    price: string,
    size: string,
    side: SideResponse
  ) => void;
  instrumentName?: string;
  sizeDecimals: number;
  priceDecimals: number;
  filter?: OrderbookFilterEnum;
  showValueInUSD: boolean;
  orderbookTickSize?: number;
}

function OrderbookSection({
  onOrderbookRowClick,
  instrumentName,
  sizeDecimals,
  priceDecimals,
  filter,
  showValueInUSD,
  orderbookTickSize,
}: ITradeSectionProps) {
  const orderbookSectionContainer = useRef<HTMLDivElement>(null);
  const midRowRef = useRef<HTMLDivElement>(null);

  const { data: orderData } = useOrder();
  const { orderbook } = useOrderbookWSS(instrumentName);

  const { data: trades } = useTradesWSS(instrumentName, undefined, true);
  const decimals = useMarketDecimals(instrumentName);
  const lastTradedPrice = useMemo(() => {
    const raw = trades?.[0]?.price;
    if (!raw) return 0;
    // REST/API trade prices are already display-ready, even when they are
    // integer strings such as "2280". Only convert genuinely raw on-chain
    // integers so the middle row does not render valid prices as $0.00.
    if (!looksRawInteger(raw)) return Number(raw);
    // 0 is this row's existing "no price yet" value. Converting at a guessed
    // scale instead printed a confident, wrong last-traded price.
    if (!decimals) return 0;
    return Number(rawToDecimal(raw, decimals.quoteDecimals));
  }, [trades, decimals]);
  const prevlastTradedPrice = usePrevious(lastTradedPrice);

  const [isPositiveLastTradedChange, setIsPositiveMarkChange] =
    useState<boolean>();

  const containerHeight = orderbookSectionContainer.current?.clientHeight || 0;
  const obHeightWithoutHeaders =
    containerHeight - COMPONENTS.perpsTableRowSmall * 2;
  const maxRows =
    !filter && orderbookSectionContainer.current?.clientHeight
      ? Math.max(
          Math.floor(
            obHeightWithoutHeaders / 2 / COMPONENTS.perpsTableRowSmall
          ),
          0
        )
      : undefined;

  const newRowHeight = maxRows
    ? obHeightWithoutHeaders / (maxRows * 2)
    : COMPONENTS.perpsTableRowSmall;

  // Parse levels
  let bids: IPriceSize[] = (orderbook?.bids as IPriceSize[]) || [];
  let asks: IPriceSize[] = (orderbook?.asks as IPriceSize[]) || [];

  // Group by tick size
  bids = orderbookTickSize ? groupByTicketSize(bids, orderbookTickSize) : bids;
  asks = orderbookTickSize
    ? groupByTicketSize(asks, orderbookTickSize, true)
    : asks;

  // Slice max rows
  // If theres a max rows, make sure both side is the same after grouping
  if (maxRows) {
    bids = bids.slice(0, maxRows);
    asks = asks.slice(0, maxRows);

    const diff = bids.length - asks.length;
    if (diff > 0) {
      // Positive diff means bids > asks
      asks = [...asks, ...Array(Math.abs(diff)).fill(["0", "0"])];
    } else if (diff < 0) {
      bids = [...bids, ...Array(Math.abs(diff)).fill(["0", "0"])];
    }
  }

  // Only update mark changes when its different
  useEffect(() => {
    const change = prevlastTradedPrice
      ? lastTradedPrice - prevlastTradedPrice
      : undefined;
    setIsPositiveMarkChange((prev) => {
      if (!change || change === 0) {
        return prev;
      }
      return change > 0;
    });
  }, [lastTradedPrice, prevlastTradedPrice]);

  // When instrument or filter changed, scroll mark price into view
  useEffect(() => {
    if (filter === OrderbookFilterEnum.BIDS) {
      orderbookSectionContainer.current?.scrollTo({ top: 0 });
    } else if (filter === OrderbookFilterEnum.OFFERS) {
      orderbookSectionContainer.current?.scrollTo({ top: 99999 });
    }
  }, [instrumentName, filter]);

  return (
    <ScrollableWrapper>
      <ScrollableContent
        ref={orderbookSectionContainer}
        style={{ overflow: filter ? "auto" : "hidden" }}
      >
        <TradeOrderbook
          indexPriceCollar={INDEX_PRICE_COLLAR_PERPS}
          headerRowHeight={`${COMPONENTS.perpsTableRowSmall}px`}
          rowHeight={`${newRowHeight}px`}
          showIV={false}
          filter={filter}
          accountOrders={orderData || []}
          instrumentId={instrumentName}
          orderbookTicksize={orderbookTickSize}
          bids={bids}
          asks={asks}
          sizeDecimals={sizeDecimals}
          priceDecimals={priceDecimals}
          onRowClick={onOrderbookRowClick}
          showValueInUSD={showValueInUSD}
          middleRow={
            <MidRow
              ref={midRowRef}
              isPositiveChange={
                lastTradedPrice ? isPositiveLastTradedChange : undefined
              }
            >
              {/* Three DISTINCT numbers, never conflated: Last is the most
                  recent trade, Mid is (bestBid+bestAsk)/2, Spread is ask−bid.
                  Only the spread PERCENTAGE is shown inline — the absolute is in
                  the tooltip. That is what buys the room for all three to fit a
                  280px panel without the "%" being clipped off the edge. */}
              {(() => {
                const { mid, spread, spreadPct } = computeSpreadStats(
                  bids,
                  asks
                );
                const fmt = (v: number) =>
                  currency(v, { precision: priceDecimals }).format();
                const spreadAbs =
                  spread === null
                    ? null
                    : priceDecimals > 0
                      ? spread.toFixed(priceDecimals)
                      : Math.round(spread).toString();
                return (
                  <>
                    <MidMetric
                      style={{ flexShrink: 0, color: "inherit" }}
                      title={
                        lastTradedPrice
                          ? `Last traded ${fmt(lastTradedPrice)}`
                          : "No trades yet"
                      }
                    >
                      <MidMetricLabel>Last</MidMetricLabel>
                      <span>{lastTradedPrice ? fmt(lastTradedPrice) : "—"}</span>
                      {lastTradedPrice ? <ArrowDown /> : null}
                    </MidMetric>
                    <MidMetric
                      title={
                        mid !== null
                          ? `Mid ${fmt(mid)} — midpoint of best bid and best ask`
                          : "Mid needs a two-sided book"
                      }
                    >
                      <MidMetricLabel>Mid</MidMetricLabel>
                      <MidMetricValue>
                        {mid !== null ? fmt(mid) : "—"}
                      </MidMetricValue>
                    </MidMetric>
                    {spreadPct !== null && (
                      <MidMetric
                        style={{ flexShrink: 0 }}
                        title={`Spread ${spreadAbs} (${spreadPct.toFixed(2)}% of best ask)`}
                      >
                        <MidMetricLabel>Spread</MidMetricLabel>
                        <MidMetricValue>
                          {spreadPct.toFixed(2)}%
                        </MidMetricValue>
                      </MidMetric>
                    )}
                  </>
                );
              })()}
            </MidRow>
          }
        />
      </ScrollableContent>
    </ScrollableWrapper>
  );
}

export default OrderbookSection;
